class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add("animate", "active");
    });

    this.addEventListener(
      "transitionend",
      () => {
        const containerToTrapFocusOn = this.classList.contains("is-empty")
          ? this.querySelector(".drawer__inner-empty")
          : document.getElementById("CartDrawer");
        const focusElement =
          this.querySelector(".drawer__inner") ||
          this.querySelector(".drawer__close");
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add("overflow-hidden");
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector(".drawer__inner").classList.contains("is-empty") &&
      this.querySelector(".drawer__inner").classList.remove("is-empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Update recommendations after cart drawer is re-rendered
    fetch(
      `/recommendations/products?section_id=product-recommendations&product_id=${this.productId}&intent=related`
    )
      .then((res) => res.text())
      .then((html) => {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        const newRecommendations = tempDiv.querySelector(".related-products");
        const recommendationsContainer =
          document.querySelector(".related-products");
        if (newRecommendations && recommendationsContainer) {
          recommendationsContainer.innerHTML = newRecommendations.innerHTML;
        }
      });

    setTimeout(() => {
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  updateShippingProgress(cartData) {
    const shippingProgress = this.querySelector(".shipping-progress");
    if (!cartData) return;

    const currency = cartData.currency || (shippingProgress ? shippingProgress.dataset.currency : null);
    
    // Only show progress bar for CAD or USD
    if (currency !== "CAD" && currency !== "USD") {
      if (shippingProgress) {
        shippingProgress.remove();
      }
      return;
    }

    // If currency is valid but progress bar doesn't exist, we need to create it
    // This would require more complex logic, so for now we'll just return
    if (!shippingProgress) return;

    const cartTotalCents = cartData.total_price || parseInt(shippingProgress.dataset.cartTotal) || 0;
    const cartTotal = cartTotalCents / 100;

    // Get thresholds (in cents for easier comparison)
    // Free shipping: CAD $150 = 15000 cents, USD $109.50 = 10950 cents
    const thresholdShippingCAD = parseInt(shippingProgress.dataset.thresholdShippingCad) || 15000;
    const thresholdShippingUSD = parseInt(shippingProgress.dataset.thresholdShippingUsd) || 10950;
    
    const shippingThreshold = currency === "USD" ? thresholdShippingUSD : thresholdShippingCAD;
    const shippingThresholdAmount = shippingThreshold / 100;

    // Calculate progress and stages
    // Progress bar max is the shipping threshold ($150 CAD / $109.50 USD)
    const maxThreshold = shippingThresholdAmount;
    let progressPercentage = (cartTotal / maxThreshold) * 100;
    if (progressPercentage > 100) {
      progressPercentage = 100;
    }
    
    let remainingAmount, hasFreeShipping;
    
    if (cartTotal >= shippingThresholdAmount) {
      hasFreeShipping = true;
      remainingAmount = 0;
    } else {
      hasFreeShipping = false;
      remainingAmount = shippingThresholdAmount - cartTotal;
    }

    // Update progress bar width
    const progressBar = shippingProgress.querySelector(".shipping-progress__bar");
    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
      progressBar.classList.remove("shipping-progress__bar--complete", "shipping-progress__bar--partial");
      if (hasFreeShipping) {
        progressBar.classList.add("shipping-progress__bar--complete");
      }
    }

    // Update stage markers
    const stageMarkers = shippingProgress.querySelectorAll(".shipping-progress__stage-marker");
    stageMarkers.forEach((marker) => {
      const stage = marker.dataset.stage;
      const icon = marker.querySelector(".shipping-progress__stage-icon");
      
      if (stage === "shipping") {
        if (hasFreeShipping) {
          marker.classList.add("shipping-progress__stage-marker--achieved");
          if (icon) icon.textContent = "✓";
        } else {
          marker.classList.remove("shipping-progress__stage-marker--achieved");
          if (icon) icon.textContent = "●";
        }
      }
    });

    // Update message
    const messageText = shippingProgress.querySelector(".shipping-progress__text");
    if (messageText) {
      const remainingFormatted = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
      }).format(remainingAmount);

      if (hasFreeShipping) {
        messageText.textContent = "You've unlocked free shipping! 🎉";
        messageText.classList.add("shipping-progress__text--success");
      } else {
        messageText.textContent = `Add ${remainingFormatted} more for free shipping`;
        messageText.classList.remove("shipping-progress__text--success");
      }
    }

    // Update data attributes
    shippingProgress.dataset.cartTotal = cartTotalCents.toString();
    shippingProgress.dataset.currency = currency;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        
        // Update shipping progress with cart data (only for CAD/USD)
        if (cartDrawerWrapper) {
          // Fetch current cart data to update progress bar
          fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((cartData) => {
              // Only update if currency is CAD or USD
              if (cartData.currency === "CAD" || cartData.currency === "USD") {
                cartDrawerWrapper.updateShippingProgress(cartData);
              } else {
                // Remove progress bar if currency is not supported
                const shippingProgress = cartDrawerWrapper.querySelector(".shipping-progress");
                if (shippingProgress) {
                  shippingProgress.remove();
                }
              }
            })
            .catch((error) => {
              console.error("Error updating shipping progress:", error);
            });
        }
        
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch(() => {
        this.querySelectorAll('.loading-overlay').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer', '.shipping-progress'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Update shipping progress after cart update (only for CAD/USD)
          const cartDrawer = document.querySelector("cart-drawer");
          if (cartDrawer) {
            fetch(`${routes.cart_url}.js`)
              .then((response) => response.json())
              .then((cartData) => {
                // Only update if currency is CAD or USD
                if (cartData.currency === "CAD" || cartData.currency === "USD") {
                  cartDrawer.updateShippingProgress(cartData);
                } else {
                  // Remove progress bar if currency changed to non-supported currency
                  const shippingProgress = cartDrawer.querySelector(".shipping-progress");
                  if (shippingProgress) {
                    shippingProgress.remove();
                  }
                }
              })
              .catch((error) => {
                console.error("Error updating shipping progress:", error);
              });
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
